import { memo, useMemo } from 'react';
import { useClassifications } from '../../../hooks/potree/useRenderer';
import { useClassificationTranslations } from '../../../hooks/translations/useClassifications';
import { ConvertType, useFormatNumber } from '../../../hooks/useFormatNumber';
import { T } from '../../../translation/src';
import { StaticColorProperty } from './ColorProperties';
import { PropertiesSection, Property } from './PropertiesSection';
import { PropertyPanel } from '../../Properties/components/PropertyPanel';
import { AnnotationType } from '../../Properties/types';
import { ICadPoint } from '../../../utils/CADFileParser';
import { useParams } from 'react-router-dom';
import { getPointPositionProperty, getPointPickColorProperty, getPointClassificationProperty, getPointIntensityProperty } from '@pointorama/potree-shared';
import { usePropertyValue } from '../../Properties/reactive/usePropertyValue';
import { hexToColor } from '../../../utils/colorUtils';

interface PointPropertiesProps {
  identifier: string;
}
const PointProperties_: React.FC2<PointPropertiesProps> = ({ identifier }) => {
  const { projectId = '' } = useParams();
  const [{ classifications }] = useClassifications();
  const { formatNumber } = useFormatNumber();

  const position = usePropertyValue(getPointPositionProperty(identifier));
  const pickColorHex = usePropertyValue(getPointPickColorProperty(identifier));
  const classificationCode = usePropertyValue(getPointClassificationProperty(identifier));
  const intensity = usePropertyValue(getPointIntensityProperty(identifier));

  const pickColor = useMemo(() => (pickColorHex ? hexToColor(pickColorHex) : null), [pickColorHex]);

  const classification = useMemo(() => {
    return (classificationCode && classifications?.find((c) => c.code === classificationCode)) || undefined;
  }, [classificationCode, classifications]);

  const classificationTranslations = useClassificationTranslations();

  return (
    <div className="flex flex-col space-y-4 divide-y divide-gray-800">
      <PropertiesSection title={<T _str="information" swc />}>
        <Property title={<T _str="type" swc />}>
          <T _str="point" swc />
        </Property>
        <Property title={<T _str="classification" swc />}>{classification ? classificationTranslations[classification.name] : '-'}</Property>
        <Property title={<T _str="intensity" swc />}>{intensity !== undefined ? Math.round(intensity) : '-'}</Property>
        <StaticColorProperty color={pickColor} />
      </PropertiesSection>
      <PropertiesSection title={<T _str="measurements" swc />}>
        <Property title={<T _str="x" swc />}>{formatNumber(position.x, { convertType: ConvertType.DISTANCE })}</Property>
        <Property title={<T _str="y" swc />}>{formatNumber(position.y, { convertType: ConvertType.DISTANCE })}</Property>
        <Property title={<T _str="z" swc />}>{formatNumber(position.z, { convertType: ConvertType.DISTANCE })}</Property>
      </PropertiesSection>
      <PropertyPanel projectId={projectId} annotationTypes={[AnnotationType.POINT]} objectIdentifiers={[identifier]} />
    </div>
  );
};
export const PointProperties = memo(PointProperties_);

const CadPointProperties_: React.FC2<{ cadPoint: ICadPoint }> = ({ cadPoint }) => {
  const { formatNumber } = useFormatNumber();

  return (
    <div className="flex flex-col space-y-4 divide-y divide-gray-800">
      <PropertiesSection title={<T _str="information" swc />}>
        <Property title={<T _str="type" swc />}>
          <T _str="point" swc />
        </Property>
      </PropertiesSection>
      <PropertiesSection title={<T _str="measurements" swc />}>
        <Property title={<T _str="x" swc />}>{formatNumber(cadPoint.position.x, { convertType: ConvertType.DISTANCE })}</Property>
        <Property title={<T _str="y" swc />}>{formatNumber(cadPoint.position.y, { convertType: ConvertType.DISTANCE })}</Property>
        <Property title={<T _str="z" swc />}>{formatNumber(cadPoint.position.z, { convertType: ConvertType.DISTANCE })}</Property>
      </PropertiesSection>
      <PropertiesSection title={<T _str="appearance" swc />}>
        <StaticColorProperty color={cadPoint.color} />
      </PropertiesSection>
    </div>
  );
};
export const CadPointProperties = memo(CadPointProperties_);
